
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { AuthLayout } from '../components/AuthLayout';
import { LoginForm } from './LoginForm';
import { RegisterForm } from './RegisterForm';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// List of paths that are public and don't require authentication
const PUBLIC_ROUTES = ['/create-first-business', '/business/'];

export const LoginContainer: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  // Get return path from different sources with priority
  // 1. Session storage for edit pages (most specific)
  // 2. Location state (from React Router)
  // 3. Default to portfolios
  const savedEditPath = sessionStorage.getItem('returnToEditPage');
  const fromLocationState = location.state?.from?.pathname;
  const fromPath = savedEditPath || fromLocationState || '/portfolios';
  
  // Debug - Log the return path we're planning to use
  console.log('Login: Return path sources:', {
    sessionStorage: savedEditPath,
    locationState: fromLocationState,
    resolved: fromPath
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Check if we're redirecting to a public route
        const isPublicRoute = PUBLIC_ROUTES.some(route => 
          fromPath === route || fromPath.startsWith(route)
        );
        
        // Always allow redirects to business edit pages
        const isBusinessEditPath = fromPath.includes('/business/') && fromPath.includes('/edit');
        
        // If it's an edit page or not a public route, proceed with redirect
        if (isBusinessEditPath || !isPublicRoute) {
          console.log('Login: Redirecting to', fromPath);
          
          // Clear the session storage after using it
          if (savedEditPath) {
            sessionStorage.removeItem('returnToEditPage');
          }
          
          navigate(fromPath, { replace: true });
        }
      }
    });

    return () => unsubscribe();
  }, [auth, navigate, fromPath, savedEditPath]);

  const handleToggleForm = () => {
    setIsLogin(!isLogin);
  };

  return (
    <AuthLayout>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{isLogin ? 'Login' : 'Create Account'}</CardTitle>
          <CardDescription>
            {isLogin ? 'Enter your credentials to access your account' : 'Fill in your details to create an account'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {isLogin ? (
            <LoginForm 
              onToggleForm={handleToggleForm} 
              savedEditPath={savedEditPath}
              fromPath={fromPath}
            />
          ) : (
            <RegisterForm onToggleForm={handleToggleForm} />
          )}
        </CardContent>
      </Card>
    </AuthLayout>
  );
};
